import { Layers, Atom, Waves, Beaker, Zap, Rocket, Microscope, FileText, Users, Building, Shield, CheckCircle } from 'lucide-react';
import type { HeroContent, AboutContent, TechnologyCard, PlatformFeature, ClinicalApplication, PatentCard, ManufacturingCard, CapabilityItem } from '../types/home.types';
import techSolubilization from '@/assets/tech-solubilization.png';
import techProtection from '@/assets/tech-protection.png';
import techLymphatic from '@/assets/tech-lymphatic.png';

export const heroData: HeroContent = {
  heading: "The pharmaceutical industry needs better oral delivery solutions for poorly water-soluble drugs and biopharmaceuticals",
  description: "Xenira Is Here To Deliver",
  image: {
    src: "/lovable-uploads/cdb18ad1-9776-4cb7-8f8b-8e10d24e68b2.png",
    alt: "Xenira pharmaceutical molecular structure visualization",
  }
};

export const aboutData: AboutContent = {
  title: "Our Company",
  subtitle: "About Us",
  description: "Xenira is a drug delivery technology company focused on transforming active agents into novel and commercially attractive therapeutic products.",
  imageUrl: "/xenira-lab.jpg",
  imageAlt: "Xenira pharmaceutical research facility showcasing advanced drug delivery technology",
  ctaText: "Learn more",
  ctaRoute: "/about",
  content: {
    heading: "Scientifically Proven Lipid-Based Delivery Platforms",
    description: "Our proprietary lipid-based drug delivery systems are scientifically proven to enhance the solubility, stability, and bioavailability of challenging active ingredients, including small molecules and biotherapeutics. These platforms support the development of value-added generic and differentiated pharmaceutical products for global markets. We offer flexible collaboration models, including technology transfer or in-house EU-GMP manufacturing."
  }
};

export const technologyData: TechnologyCard[] = [
  {
    id: 1,
    title: "Solubilization & Self-Emulsification",
    description: "Lipid formulations keep active ingredients in a solubilized state, forming colloidal or nano-sized droplets that support improved absorption.",
    category: "Research",
    image: techSolubilization
  },
  {
    id: 2,
    title: "Protection from Degradation",
    description: "Lipid matrices shield APIs from gastric acid and enzymatic breakdown, helping preserve stability and supporting enhanced bioavailability.",
    category: "Manufacturing",
    image: techProtection
  },
  {
    id: 3,
    title: "Lymphatic Transport",
    description: "For highly lipophilic compounds, certain lipid-based systems enable lymphatic uptake, allowing partial bypass of first-pass metabolism.",
    category: "Partnerships",
    image: techLymphatic
  }
];

export const platformData: PlatformFeature[] = [
  {
    Icon: Layers,
    name: "NLC & SLN",
    description: "Nanostructured lipid carriers and solid lipid nanoparticles enable high drug loading capacity, controlled release, and improved stability through lipid matrix encapsulation, supporting enhanced bioavailability.",
    background: <div />,
    className: "col-span-1"
  },
  {
    Icon: Atom,
    name: "Liposomes",
    description: "Phospholipid-based vesicular systems with well-established biocompatibility, suitable for targeted drug delivery, supporting improved therapeutic performance.",
    background: <div />,
    className: "col-span-1"
  },
  {
    Icon: Waves,
    name: "SEDDS / SNEDDS",
    description: "Self-emulsifying drug delivery systems that spontaneously form fine emulsions or nanoemulsions in the gastrointestinal tract, improving the oral absorption of poorly water-soluble drugs.",
    background: <div />,
    className: "col-span-1"
  },
  {
    Icon: Beaker,
    name: "Lipid-based Nanoemulsions",
    description: "Advanced nanoemulsion systems designed to enhance solubility and support improved bioavailability of APIs, while maintaining long-term physical stability.",
    background: <div />,
    className: "col-span-1"
  }
];

export const clinicalApplicationsData: ClinicalApplication[] = [
  { text: "Enabling oral delivery of APIs that traditionally required injection or complex administration" },
  { text: "Enhancing bioavailability and supporting consistent therapeutic performance of existing oral drugs" },
  { text: "Protecting sensitive molecules, supporting the development of next-generation oral formulations" },
  { text: "Supporting reduced dosing frequency and improved patient convenience and compliance through optimized formulations" },
  { text: "Facilitating lifecycle management of established drugs with improved delivery profiles" }
];

export const patentData: PatentCard[] = [
  {
    icon: Zap,
    title: "Proprietary technologies beyond conventional formulation tools",
    description: "Innovative lipid platforms and formulation strategies that provide enhanced performance compared to standard drug delivery methods.",
    callouts: ["Enzymatic Degradation Protection", "Lymphatic Uptake", "Oral, Patient-Friendly Delivery"]
  },
  {
    icon: Atom,
    title: "Custom lipid excipient selection for your molecule",
    description: "Tailored lipid matrix selection and optimization designed to improve the bioavailability, stability, and delivery profile of your active pharmaceutical ingredient.",
    callouts: ["Increased Solubility", "Controlled Release", "Consistent Pharmacokinetic Profiles"]
  },
  {
    icon: Rocket,
    title: "Seamless support from early R&D to EU-GMP commercial manufacturing",
    description: "End-to-end partnership covering formulation design, regulatory strategy, scale-up, and commercial production under EU-GMP standards.",
    callouts: ["Formulation Expertise", "Regulatory Guidance", "Integrated Scale-Up"]
  }
];

export const manufacturingData: ManufacturingCard[] = [
  {
    id: 1,
    title: "In-House Facility",
    brand: "Development & Manufacturing",
    description: "Our in-house facility is designed for the development and manufacturing of drug products and intermediates using lipid-based delivery systems. It supports clinical supply, scale-up, and full commercial production.",
    imageUrl: "/lovable-uploads/1121baef-38ca-4498-b0a3-63742b230d11.png",
    altText: "Modern pharmaceutical manufacturing facility with EU-GMP compliance certification for lipid-based drug development"
  },
  {
    id: 2,
    title: "Controlled Substances",
    brand: "Regulatory Compliance",
    description: "The site is licensed for the handling of controlled substances, enabling the development, testing, and manufacturing of therapies under strict regulatory oversight. This ensures full compliance with the highest standards of safety, security, and quality.",
    imageUrl: "/lovable-uploads/cb6d3f0a-068d-4fc4-8277-0ceabc1c5207.png",
    altText: "Secure controlled substances handling area in pharmaceutical laboratory with regulatory compliance measures"
  },
  {
    id: 3,
    title: "Quality System",
    brand: "GxP Standards",
    description: "A unified quality system governs every stage of the process, from supplier qualification to batch release — fully aligned with EMA and ICH standards. All team members are trained in GxP principles, ensuring that quality is embedded across the organization from day one.",
    imageUrl: "/lovable-uploads/09708523-d3c7-4f1b-82b9-264db1929cc0.png",
    altText: "Quality control laboratory with advanced testing equipment for pharmaceutical batch validation and GxP compliance"
  }
];

export const capabilitiesData: CapabilityItem[] = [
  {
    icon: <Atom className="h-8 w-8 md:h-10 md:w-10 text-[hsl(var(--color-3))]" />,
    title: "Design of Lipid-Based Drug Delivery Systems",
    description: "We engineer proprietary LBDDS platforms to address solubility, stability, and absorption challenges — enabling oral delivery of small molecules and biopharmaceuticals."
  },
  {
    icon: <Microscope className="h-8 w-8 md:h-10 md:w-10 text-[hsl(var(--color-3))]" />,
    title: "Preclinical Evaluation",
    description: "Our delivery systems are scientifically evaluated through in vitro and in vivo studies, including pharmacokinetic modeling and intestinal barrier assays."
  },
  {
    icon: <FileText className="h-8 w-8 md:h-10 md:w-10 text-[hsl(var(--color-3))]" />,
    title: "Patent Protection",
    description: "We implement strategic, international IP frameworks to secure exclusivity and long-term commercial value for each technology."
  },
  {
    icon: <Zap className="h-8 w-8 md:h-10 md:w-10 text-[hsl(var(--color-3))]" />,
    title: "Development of Xenira's Own Products",
    description: "We apply our platforms to create a pipeline of pharmaceutical raw materials, value-added generics — enhancing the overall performance of established APIs."
  },
  {
    icon: <Users className="h-8 w-8 md:h-10 md:w-10 text-[hsl(var(--color-3))]" />,
    title: "Partner-Driven Innovation",
    description: "We collaborate with pharmaceutical companies to turn their active agents into novel, commercially attractive oral therapies using Xenira's proprietary technologies."
  },
  {
    icon: <Shield className="h-8 w-8 md:h-10 md:w-10 text-[hsl(var(--color-3))]" />,
    title: "Regulatory Strategy & Registration",
    description: "Once proof-of-concept is achieved, we prepare CTD dossiers in line with ICH guidelines — designed to support EMA and global regulatory submissions."
  },
  {
    icon: <CheckCircle className="h-8 w-8 md:h-10 md:w-10 text-[hsl(var(--color-3))]" />,
    title: "Licensing & Technology Transfer",
    description: "We offer flexible out-licensing models — with or without technology transfer — allowing partners to advance product development using proven, IP-secured platforms."
  },
  {
    icon: <Building className="h-8 w-8 md:h-10 md:w-10 text-[hsl(var(--color-3))]" />,
    title: "EU-GMP Manufacturing",
    description: "Our in-house cleanroom facility, built to EU-GMP standards, supports contract manufacturing of clinical trial material, process qualification, and commercial production."
  }
];
